import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Where a page was opened from, so "Back", Cancel and Save return there rather
 * than to a fixed page (design-system rule, 2026-09-22). A profile opened from
 * a team goes back to the team; opened from a filtered list, back to that list
 * with its search, sort and page intact.
 *
 * The origin rides in router state, set by links that pass `useFromHere()`.
 * Only same-app paths are trusted, so state cannot redirect off the portal.
 */
interface FromState {
  readonly from: string;
}

/** The origin recorded in router state, when it is an in-app path. */
function originOf(state: unknown): string | undefined {
  if (typeof state !== 'object' || state === null || !('from' in state)) return undefined;
  const from = (state as { from: unknown }).from;
  return typeof from === 'string' && from.startsWith('/') && !from.startsWith('//') ? from : undefined;
}

/**
 * Router state for a link leaving this page: `<Link to=… state={useFromHere()}>`.
 *
 * @returns This page's path and query, as the next page's origin.
 */
export function useFromHere(): FromState {
  const location = useLocation();
  return { from: `${location.pathname}${location.search}` };
}

/**
 * Where "back" goes from this page.
 *
 * @param fallback - Used when the page was opened directly (a bookmark, a
 *   shared link, a reload) and so has no in-app origin.
 * @returns The origin if there is one, else the fallback.
 */
export function useReturnTo(fallback: string): string {
  return originOf(useLocation().state) ?? fallback;
}

/**
 * Returns to where this page was opened from: one step back in history when
 * the origin is known (restoring that page exactly, its own origin included),
 * otherwise to the fallback. For Save, Cancel and other ways out of a page.
 *
 * @param fallback - Where to go when the page was opened directly.
 * @returns A function that leaves the page.
 */
export function useGoBack(fallback: string): () => void {
  const location = useLocation();
  const navigate = useNavigate();
  const origin = originOf(location.state);
  return () => {
    if (origin === undefined) navigate(fallback);
    else navigate(-1);
  };
}
